<template>
  <div class="featured-content pb-4">
    <div
      v-if="noIntroContent && !useTwoArticleVariant"
      class="featured-content-wrapper"
    >
      <h2
        v-if="title"
        class="featured-content-title"
      >
        {{ title }}
      </h2>
      <p
        v-if="description"
        class="featured-content-description"
      >
        {{ description }}
      </p>
    </div>

    <div class="card-overview">
      <!-- Mobile carousel when two-article variant -->
      <div
        v-if="useTwoArticleVariant"
        class="md:hidden"
      >
        <div class="flex flex-row gap-2">
          <h2
            v-if="title"
            class="text-2xl text-black"
          >
            {{ title }}
          </h2>
          <div class="relative w-20 h-16 flex justify-center items-center">
            <ShieldSmall class="w-16 h-16 text-success" />
            <span
              v-if="rating"
              class="absolute inset-0 flex items-center justify-center text-3xl font-bold text-white"
            >
              {{ rating }}
            </span>
          </div>
        </div>
        <p
          v-if="description"
          class="mb-6"
        >
          {{ description }}
        </p>
      </div>
      <vue-glide
        v-if="useTwoArticleVariant"
        class="block md:hidden carousel"
        :type="carouselType"
        :start-at="carouselStartAt"
        :bound="carouselBound"
        :rewind="carouselRewind"
        :focus-at="carouselFocusAt"
        :per-view="1"
        :gap="carouselGap"
        :breakpoints="carouselBreakpoints"
        :peek="carouselPeek"
      >
        <vue-glide-slide
          v-for="(card, i) in cards"
          :key="i"
          class="carousel-slide"
        >
          <kf-card
            :src="card.src"
            :alt="card.alt"
            :type="useNewLayout && 'secondary'"

            :link-url="card.linkUrl"
            :description="card.title"
            :centered="card.review"
            :review="card.review || false"
            class="group"
          >
            <template #card-body>
              <kf-heading
                v-if="card.title"
                tag="h3"
                class="card-title"
              >
                {{ card.title }}
              </kf-heading>
              <span
                v-if="card.label"
                class="card-label"
              >
                {{ card.label }}
              </span>
              <p
                v-if="card.subtitle"
                class="card-subtitle"
              >
                {{ card.subtitle }}
              </p>
              <p
                v-if="card.description && truncateIntro"
                :class="{ italic: card.descriptionIsQuote }"
              >
                {{ card.description | truncate(109, '...') }}
              </p>
              <p
                v-if="card.description && !truncateIntro"
                :class="{ italic: card.descriptionIsQuote }"
              >
                {{ card.description }}
              </p>
              <span
                v-if="i18n && i18n.cardlabel"
                class="card-link-span group-hover:underline"
              >
                {{ i18n.cardlabel }}
              </span>
              <span
                v-else-if="card.linkLabel"
                class="card-link-span group-hover:underline"
              >
                {{ card.linkLabel }}
              </span>
            </template>
          </kf-card>
        </vue-glide-slide>
      </vue-glide>

      <!-- Mobile list when not two-article variant -->
      <div
        v-if="!useTwoArticleVariant"
        class="block md:hidden space-y-6"
      >
        <kf-card
          v-for="(card, i) in cards"
          :key="i"
          :type="useNewLayout && 'secondary'"
          :src="card.src"
          :label="card.label"
          :alt="card.alt"
          :link-url="card.linkUrl"
          :description="card.title"
          :centered="card.review"
          :review="card.review || false"
          class="group"
        >
          <template #card-body>
            <kf-heading
              v-if="card.title"
              tag="h3"
              class="card-title"
            >
              {{ card.title }}
            </kf-heading>
            <span
              v-if="card.label"
              class="card-label"
            >
              {{ card.label }}
            </span>
            <p
              v-if="card.subtitle"
              class="card-subtitle"
            >
              {{ card.subtitle }}
            </p>
            <p
              v-if="card.description && truncateIntro"
              :class="{ italic: card.descriptionIsQuote }"
            >
              {{ card.description | truncate(109, '...') }}
            </p>
            <p
              v-if="card.description && !truncateIntro"
              :class="{ italic: card.descriptionIsQuote }"
            >
              {{ card.description }}
            </p>
            <span
              v-if="i18n && i18n.cardlabel"
              class="card-link-span group-hover:underline"
            >
              {{ i18n.cardlabel }}
            </span>
            <span
              v-else-if="card.linkLabel"
              class="card-link-span group-hover:underline"
            >
              {{ card.linkLabel }}
            </span>
          </template>
        </kf-card>
      </div>

      <!-- Desktop grid overview -->
      <div class="hidden md:grid grid-cols-1 md:grid-cols-3 md:gap-8 lg:gap-16">
        <div
          v-if="useTwoArticleVariant"
          class="col-span-1 flex flex-col justify-center"
        >
          <div class="flex flex-row gap-2">
            <h2
              v-if="title"
              class="text-2xl text-black"
            >
              {{ title }}
            </h2>
            <div class="relative w-16 h-16 flex-1">
              <ShieldSmall class="w-16 h-16 text-success" />
              <span
                v-if="rating"
                class="absolute inset-0 flex items-center justify-center text-3xl font-bold text-white"
              >
                {{ rating }}
              </span>
            </div>
          </div>
          <p
            v-if="description"
            class="mb-6"
          >
            {{ description }}
          </p>
          <kf-link
            v-if="link"
            :type="link.type"
            :href="link.href"
            :target="link.target"
            class="text-one-column-link"
          >
            {{ link.label }}
          </kf-link>
        </div>
        <kf-card
          v-for="(card, i) in displayedCards"
          :key="i"
          :src="card.src"
          :alt="card.alt"
          :type="useNewLayout && 'secondary'"
          :link-url="card.linkUrl"
          :description="card.title"
          :centered="card.review"
          :review="card.review || false"
          :class="`card-${i}`"
          class="group"
        >
          <template #card-body>
            <kf-heading
              v-if="card.title"
              tag="h3"
              class="card-title"
            >
              {{ card.title }}
            </kf-heading>

            <span
              v-if="card.label"
              class="card-label"
            >
              {{ card.label }}
            </span>

            <p
              v-if="card.subtitle"
              class="card-subtitle"
            >
              {{ card.subtitle }}
            </p>
            <p
              v-if="card.description && truncateIntro"
              :class="{ italic: card.descriptionIsQuote }"
            >
              {{ card.description | truncate(109, '...') }}
            </p>
            <p
              v-if="card.description && !truncateIntro"
              :class="{ italic: card.descriptionIsQuote }"
            >
              {{ card.description }}
            </p>
            <span
              v-if="i18n && i18n.cardlabel"
              class="card-link-span group-hover:underline"
              :class="useNewLayout && 'btn btn-secondary'"
            >
              {{ i18n.cardlabel }}
              <ArrowRight
                v-if="useNewLayout"
                class="inline-block w-4 h-4 ml-2"
              />
            </span>
            <span
              v-else-if="card.linkLabel"
              class="card-link-span group-hover:underline"
              :class="useNewLayout && 'btn btn-secondary'"
            >
              {{ card.linkLabel }}
              <ArrowRight
                v-if="useNewLayout"
                class="inline-block w-4 h-4 ml-2"
              />
            </span>
          </template>
        </kf-card>
      </div>
    </div>

    <div
      v-if="link && !useTwoArticleVariant"
      class="featured-content-btns"
    >
      <kf-link
        :type="link.type"
        :href="link.href"
        :target="link.target"
        class="text-one-column-link"
      >
        {{ link.label }}
      </kf-link>
    </div>
  </div>
</template>

<script>
import KfHeading from '@/components/base/heading/Heading.vue';
import KfLink from '@/components/base/link/Link.vue';
import KfCard from '@/components/card/Card.vue';
import ArrowRight from '@/components/svg/ArrowRight.vue';
import ShieldSmall from '@/components/svg/ShieldSmall.vue';
import GlobalMixins from '@/mixins/GlobalMixins.js';
import { Glide, GlideSlide } from 'vue-glide-js';
import 'vue-glide-js/dist/vue-glide.css';

export default {
  name: 'KfFeaturedContent',
  components: {
    KfCard,
    KfHeading,
    KfLink,
    ShieldSmall,
    [Glide.name]: Glide,
    [GlideSlide.name]: GlideSlide,
    ArrowRight,
  },
  mixins: [GlobalMixins],
  props: {
    rating: {
      type: [String, Number],
      default: null,
    },
    useNewLayout: {
      type: Boolean,
      default: false,
    },
    useTwoArticleVariant: {
      type: Boolean,
      default: false,
    },
    i18n: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    description: {
      type: String,
      default: null,
    },
    descriptionIsQuote: {
      type: Boolean,
      default: false,
    },
    cards: {
      type: Array,
      default: () => [],
    },
    link: {
      type: Object,
      default: null,
    },
    truncateIntro: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      carouselType: 'slider',
      carouselStartAt: 0,
      carouselBound: false,
      carouselRewind: false,
      carouselFocusAt: 0,
      carouselGap: 16,
      carouselBreakpoints: {},
      carouselPeek: { before: 0, after: 60 },
    };
  },
  computed: {
    noIntroContent() { return this.title || this.description; },
    hasLink() { return !!this.link; },
    displayedCards() { return this.useTwoArticleVariant ? this.cards.slice(0, 2) : this.cards; },
  },
};
</script>

<style lang="postcss">
.featured-content {
  @apply relative;

  &-wrapper {
    @screen md {
      @apply mx-auto max-w-3xl;
    }
  }

  &-title,
  &-description {
    @apply text-center;
  }

  &-description {
    @apply mb-8;
  }

  &-btns {
    @apply flex justify-center mt-8;

    @screen md {
      @apply mt-12;
    }
  }
}

/* Carousel overrides */
.carousel {
  @apply relative;
  overflow: visible;

  .glide__track {
    @apply pr-5 pl-0;
    overflow: visible;
  }
  .glide__slides {
    @apply list-none pb-5;
    overflow: visible;
  }
  .glide__slide {
    @apply relative h-auto;
    overflow: visible;
  }

  /* Show full kf-card overflow */
  ::v-deep .kf-card,
  ::v-deep .kf-card > *,
  ::v-deep .kf-card .card-body,
  ::v-deep .kf-card .card-content {
    overflow: visible;
  }
}
</style>
